import React, { useEffect, useState } from 'react';
import { Grid, Box, Typography, Checkbox } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SMSEchos from './echos/smsEchos';
import { CustomButton, ConfirmationModal } from '../../../shared';
import { getCampaignDetails, getAllCampaigns } from '../../../../redux/thunks/campaignThunks';
import { createMessage, updateMessage } from '../../../../redux/thunks/smsCampaignThunk';

const useStyles = makeStyles((theme) => ({
    root: {
        '&$checked': {
            color: '#8C3F7D',
        },
    },
    checked: {},
}));

const smsSchema = yup
    .object({
        name: yup.string().required('Name is required'),
        message: yup.string().required('Message is required'),
        isChecked: yup.boolean().default(false),
    })
    .required();

function SMSCampaign(props) {
    const { campaignId, handleClose, handleUpdateCampaign } = props;
    const [showModal, setShowModal] = useState(false);
    const dispatch = useDispatch();
    const { company } = useSelector((state) => state.company);
    const { campaign } = useSelector((state) => state.campaign);
    const { message } = useSelector((state) => state.smsCampaign);
    const classes = useStyles();
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors, isValid },
    } = useForm({
        resolver: yupResolver(smsSchema),
        defaultValues: {
            name: '',
            message: '',
            isChecked: false,
        },
    });

    const onSubmit = (data) => {
        if(data.isChecked) {
            let smsCampaignData = {
                name: data.name,
                message: data.message
            }
            if (campaign.message === null || campaign.message?.id === null) {
                dispatch(createMessage({
                    companyId: company.id,
                    campaignId: campaignId,
                    data: smsCampaignData,
                })).then(()=> {
                    dispatch(
                        getCampaignDetails({
                            companyId: company.id,
                            campaignId: campaignId,
                        })
                    );
                }).catch(error => console.error("Error in Creating Message for SMS Campaign", error));
            } else {
                dispatch(updateMessage({
                    companyId: company.id,
                    campaignId: campaignId,
                    data: smsCampaignData,
                    messageId: campaign.message.id,
                })).then(async()=> {
                    await handleUpdateCampaign();
                    handleClose();
                    dispatch(getAllCampaigns({ companyId: company.id }));
                }).catch(error => console.error("Error in Updating Message for SMS Campaign", error));
            }
        } else {
            setShowModal(!showModal);
        }
    };

    const handleSave = () => {
        handleSubmit(onSubmit);
    };

    const handleFinalizeClick = () => {
        if (isValid && getValues('isChecked') === true) {
            let smsCampaignData = {
                name: getValues('name'),
                message: getValues('message'),
            }
            if (campaign.message === null || campaign.message?.id === null) {
                dispatch(createMessage({
                    companyId: company.id,
                    campaignId: campaignId,
                    data: smsCampaignData,
                })).then(async()=> {
                    await handleUpdateCampaign();
                    handleClose();
                    dispatch(getAllCampaigns({ companyId: company.id }));
                }).catch(error => console.error("Error in Creating Message for SMS Campaign", error));
            } else {
                dispatch(updateMessage({
                    companyId: company.id,
                    campaignId: campaignId,
                    data: smsCampaignData,
                    messageId: campaign.message.id,
                })).then(async()=> {
                    await handleUpdateCampaign();
                    handleClose();
                    dispatch(getAllCampaigns({ companyId: company.id }));
                }).catch(error => console.error("Error in Updating Message for SMS Campaign", error));
            }
        } else {
            setShowModal(!showModal);
        }
    };

    useEffect(()=> {
        if (campaignId !== campaign.id) {
            dispatch(getCampaignDetails({companyId: company.id, campaignId: campaignId}))
        }
    }, []);

    useEffect(()=> {
        if (campaign && campaign.message) {
            setValue('name', campaign.message.name);
            setValue('message', campaign.message.message);
        }
    }, [campaign, campaignId, company]);
    
    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Box className="padding-24px pt-0">
                        <Box className="pb-12px">
                            <Typography className="text-lg-bold gray-900">
                                SMS Campaign
                            </Typography>
                        </Box>
                        <Box className="campaign-message-container pb-12px">
                            <Box className="flex-row-center-start gap-6">
                                <Box className="padding-10px">
                                    <ErrorIcon />
                                </Box>
                                <Typography className="gray-900 text-md-regular">
                                    This is a live campaign, please only upload/add
                                    any content that has been approved by the
                                    Medical, Legal and Regulatory reviewers. Unless
                                    final approval from the reviewers is received,
                                    please do not upload any content.
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <form id="smsCampaign" onSubmit={handleSubmit(onSubmit)}>
                                <Box className="pt-12px pb-12px">
                                    <Typography className="text-sm-medium gray-700">
                                        Name
                                    </Typography>
                                    <input
                                        className="custom-text-field"
                                        placeholder="Name"
                                        {...register('name')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.name?.message}
                                    </span>
                                </Box>

                                <Box className="pt-12px pb-12px">
                                    <Typography className="text-sm-medium gray-700">
                                        Message
                                    </Typography>
                                    <input
                                        className="custom-text-field"
                                        placeholder="Message"
                                        {...register('message')}
                                    />
                                    <span className="text-xs-regular error-500">
                                        {errors.message?.message}
                                    </span>
                                </Box>

                                <Box className="flex-row-start-center gap-8">
                                    <Checkbox
                                        classes={{
                                            root: classes.root,
                                            checked: classes.checked,
                                        }}
                                        {...register('isChecked')}
                                    />
                                    <Typography className="text-sm-medium gray-700">
                                        I acknowledge that this campaign has met the
                                        required reviewer approvals
                                    </Typography>
                                </Box>
                                
                                <Box className="pt-12px pb-24px">
                                    <CustomButton
                                        title="Save"
                                        titleColor="#FFFFFF"
                                        buttonColor="#AD509E"
                                        handleClick={handleSave}
                                        type="submit"
                                    />
                                </Box>
                            </form>
                        </Box>

                        {campaign.message && campaign.message.id ? (<Box> 
                            <SMSEchos />
                        </Box>) : null}

                        <Box className="pt-12px pb-24px">
                            <CustomButton
                                title="Finalize Campaign"
                                titleColor="#FFFFFF"
                                buttonColor="#AD509E"
                                handleClick={handleFinalizeClick}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <ConfirmationModal
                open={showModal}
                onClose={()=> setShowModal(!showModal)}
                content={
                    <Box>
                        <Typography className="mb-20px mt-20px" variant="body1">
                            You Need to Acknowledge this SMS Campaign
                        </Typography>
                    </Box>
                }
                title={
                    <Typography className="queries-table-title">
                        Acknowledge Campaign
                    </Typography>
                }
                primaryButtonText={'Ok'}
                isCancelShow={false}
                onConfirm={()=> setShowModal(!showModal)}
            />
        </>
    );
}

export default SMSCampaign;
